import { useEffect, useRef, useState } from "react";
import { Product, Variant } from "@/types/types.ts";
import { formatTimeToAmPm } from "@/utils/date/formatTime";
import { DMS_IDS } from "@/constants/constants";
import { isTwoManDelivery } from "@/utils/product/isTwoManDelivery";
import type { Card } from "../ReasonToBuy";
import {
  ClickAndCollectCountdownCopy,
  ClickAndCollectLogo,
  WaitroseLogo,
} from "./clickAndCollectCopy";
import {
  formatTimeLeft,
  getClickAndCollectState,
  getCutoffTime,
  getNextTickDelayMs,
} from "./clickAndCollectState";

const CARD_ID = "clickandcollect";

const defaultClickAndCollectCard: Card = {
  id: CARD_ID,
  title: "Click & Collect",
  description: "From thousands of convenient UK locations",
  icon: <ClickAndCollectLogo />,
};

export function useClickAndCollectCard(
  product: Product,
  variant: Variant,
  isSelected: boolean,
): Card | null {
  const [now, setNow] = useState<Date | null>(null);
  const loadedAtRef = useRef<Date | null>(null);

  const clickAndCollectOption = product.fulfilmentOptions?.available?.find(
    (option) => option.aliases?.dmsId === DMS_IDS.ClickAndCollect,
  );

  const orderByLocalTimeToday =
    clickAndCollectOption?.earliestFulfilment?.orderByLocalTimeToday ?? null;

  useEffect(() => {
    const loadedAt = new Date();
    loadedAtRef.current = loadedAt;
    setNow(loadedAt);
  }, []);

  useEffect(() => {
    if (!now) return;

    const cutoff = getCutoffTime(orderByLocalTimeToday, now);

    if (!cutoff) return;

    const delayMs = getNextTickDelayMs(cutoff.getTime() - now.getTime());

    if (delayMs === null) return;

    const timeoutId = setTimeout(() => setNow(new Date()), delayMs);
    return () => clearTimeout(timeoutId);
  }, [now, orderByLocalTimeToday]);

  if (!clickAndCollectOption) return null;
  if (isTwoManDelivery(product)) return null;

  const { availability } = variant;

  if (
    isSelected &&
    (!availability?.availableToOrder ||
      availability.futureAvailability ||
      availability.preOrder)
  ) {
    return null;
  }

  const loadedAt = loadedAtRef.current;

  if (!now || !loadedAt) return defaultClickAndCollectCard;

  const state = getClickAndCollectState({
    orderByLocalTimeToday,
    now,
    loadedAt,
  });

  if (state.kind === "default") return defaultClickAndCollectCard;

  if (state.kind === "countdown") {
    return {
      id: CARD_ID,
      title: "Get it tomorrow",
      description: (
        <ClickAndCollectCountdownCopy
          timeLeft={formatTimeLeft(state.timeLeftMs)}
        />
      ),
      icon: <WaitroseLogo />,
    };
  }

  return {
    id: CARD_ID,
    title: "Get it tomorrow",
    description: `Order before ${formatTimeToAmPm(orderByLocalTimeToday)} and collect from your nearest Waitrose`,
    icon: <WaitroseLogo />,
  };
}
